import ViewFactory from "@/Factories/ViewFactory";
import WebComponentView, {
    IWebComponentViewConstructorParameters
} from "@/Components/WebComponents/WebComponentView";
import WebComponentViewViewModel, {
    IWebComponentViewViewModelConstructorParameters
} from "./WebComponentViewViewModel";
import ComponentManager from "@/Management/ComponentManager";
import {
    WebComponentBindingDependencies,
    WebComponentBindingOptions,
    WebComponentBindingProperties,
} from "#/CoreFrameInterfaces";

type Constructor<T> = new (...args: any[]) => T;

export default abstract class WebComponentViewFactory<
    TOriginalComponent = unknown,
    VM extends WebComponentViewViewModel = WebComponentViewViewModel,
    V extends WebComponentView<TOriginalComponent> = WebComponentView<TOriginalComponent>
> extends ViewFactory {
    /**
     * @override
     */
    public viewModel: VM;

    /**
     * @override
     */
    public view: V;

    protected get viewModelName(): string {
        return "WebComponentView";
    }

    /**
     * @override
     */
    public viewName: string = "WebComponentView";

    protected abstract get tagName(): string;

    /**
     * @override
     */
    public getView(): JQuery<HTMLElement> {
        if (!this.viewModel)
            this.viewModel = this.createViewModel();

        if (!this.view)
            this.view = this.createView(this.viewModel);

        const tuple = this.view.getTuple();
        return tuple.view;
    }

    protected createViewModel(): VM {
        const ViewModel = ComponentManager.getViewModel<Constructor<VM>>(this.viewModelName);
        return new ViewModel(this.createViewModelParameters());
    }

    protected createViewModelParameters(): IWebComponentViewViewModelConstructorParameters {
        return {};
    }

    protected createView(viewModel: VM): V {
        const View = ComponentManager.getView<Constructor<V>>(this.viewName);
        return new View(this.createViewParameters(), viewModel);
    }

    protected createViewParameters(): IWebComponentViewConstructorParameters<TOriginalComponent> {
        return {
            tagName: this.tagName,
            bindingOptions: this.createBindingOptions(),
        }
    }

    private createBindingOptions(): WebComponentBindingOptions<TOriginalComponent> {
        return {
            dependencies: this.getDependencies(),
            properties: this.getProperties(),
        };
    }

    protected getDependencies(): WebComponentBindingDependencies {
        return {};
    }

    protected getProperties(): WebComponentBindingProperties<TOriginalComponent> {
        return {};
    }
}
